import { useCallback, useMemo } from 'react';
import { Button, Dropdown, Group, IconCaretDown, Typography, useDropdownState } from '@screentone/core';
import classnames from 'classnames';

// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { usePageTypeSettingsContext } from 'contexts/page-type-settings/usePageTypeSettings';
import { ModuleContainerUnion, PageModule, PageTypeSetting, TreatmentTypeSetting } from 'data/generated/graphql';
import {
  getModuleFieldKey,
  getSubsetTreatmentTypeSettings,
  getTreatmentType,
  useModuleTreatmentTypes
} from 'utils/modules';
import styles from './TreatmentTypeDropdown.module.scss';

interface TreatmentDropdownProps {
  hierarchyId: string;
  module: ModuleContainerUnion;
  pageModule: PageModule;
  showCurrentTreatmentTypeAtTheTop?: boolean;
  viewOnly?: boolean;
  pageTypeSetting?: PageTypeSetting;
  isReadOnly?: boolean;
}

export const TreatmentTypeDropdown = ({
  module,
  pageModule,
  hierarchyId,
  showCurrentTreatmentTypeAtTheTop,
  viewOnly,
  pageTypeSetting,
  isReadOnly = false
}: TreatmentDropdownProps) => {
  const { structureTreatmentTypes } = usePageTypeSettingsContext();
  const { changeEntity, getDTO } = useDataModelContext();

  // Collect the slotRefs of every other module in the same group so that when a module changes
  // its treatment type we can allocate the lowest free occurrence index rather than reusing the
  // old numeric suffix — which could duplicate an existing sibling's slotRef.
  const siblingSlotRefs = useMemo(() => {
    type RawModule = { type?: string; groupId?: string; slotRef?: string };
    type RawRoot = { collection: Array<{ collection: RawModule[] }> };
    const allModules = (getDTO().root as unknown as RawRoot).collection[0]?.collection ?? [];
    return allModules
      .filter((m) => m.type === 'Module' && m.groupId === module.groupId && m.slotRef && m.slotRef !== module.slotRef)
      .map((m) => m.slotRef as string);
  }, [getDTO, module.groupId, module.slotRef]);

  // SETTINGS_ONLY(structure-migration): settingsTreatmentTypes comes from hardcoded SETTINGS; empty for structure pubs.
  // New data shape (e.g. penta, no SETTINGS entry): settingsTreatmentTypes is empty, fall back to structureTreatmentTypes.
  const settingsTreatmentTypes = pageTypeSetting?.treatmentTypeSettings ?? [];
  const treatmentTypeSettings: TreatmentTypeSetting[] =
    settingsTreatmentTypes.length > 0 ? settingsTreatmentTypes : structureTreatmentTypes;
  // END SETTINGS_ONLY(structure-migration) — when deleted: replace treatmentTypeSettings with structureTreatmentTypes directly

  const onChangeNewModule = useCallback(
    (newModule: ModuleContainerUnion) => {
      changeEntity(hierarchyId, () => newModule);
    },
    [hierarchyId, changeEntity]
  );

  const moduleFieldKey = getModuleFieldKey(pageModule.uiModuleType!);
  const subsetTreatmentTypeSettings = getSubsetTreatmentTypeSettings(pageModule.uiModuleType!, treatmentTypeSettings);

  const { open, setOpen, componentRef } = useDropdownState();

  const { handleChangeTreatmentByValue, allowedTreatmentTypeSettings } = useModuleTreatmentTypes({
    moduleFieldKey,
    onChangeNewModule,
    moduleContainer: module,
    subsetTreatmentTypeSettings,
    siblingSlotRefs
  });

  const treatmentType = getTreatmentType(pageModule);
  const treatmentTypeSetting = treatmentTypeSettings.find(
    (t) => t.treatmentType === treatmentType || t.treatmentTypeKey === treatmentType
  );

  if (showCurrentTreatmentTypeAtTheTop) {
    const currentTreatmentType = pageModule.uiModuleFields?.[moduleFieldKey]?.treatmentType;

    const currTreatmentTypeIndex = allowedTreatmentTypeSettings?.findIndex(
      (treatmentTypeSetting) =>
        (treatmentTypeSetting.treatmentTypeKey ?? treatmentTypeSetting.treatmentType) ===
        (currentTreatmentType as string)
    );

    allowedTreatmentTypeSettings?.unshift(allowedTreatmentTypeSettings[currTreatmentTypeIndex!]);
    allowedTreatmentTypeSettings?.splice(currTreatmentTypeIndex! + 1, 1);
  }

  // @ts-expect-error Should be fine to do a less than or equal check here
  const disabled = allowedTreatmentTypeSettings?.length <= 1 || viewOnly;
  const handleDropdownOpenToggle = () => {
    if (!disabled) setOpen(!open);
  };

  return disabled ? (
    <Typography
      data-testid="treatment-type-label-disabled"
      color="asphalt"
      variant="note"
      margin={{ bottom: 'none' }}
      className={classnames(`st_focusable, ${styles.triggerDisabled}`)}
    >
      {treatmentTypeSetting?.label}
    </Typography>
  ) : (
    <Dropdown
      open={open}
      onToggle={handleDropdownOpenToggle}
      componentRef={componentRef}
      data-testid="treatment-type-dropdown"
    >
      <Dropdown.Trigger>
        <Group
          direction="row"
          gap="xs"
          className={classnames(`st_focusable ${styles.trigger}`)}
          componentEl="button"
          data-testid="treatment-type-trigger"
        >
          <Typography data-testid="treatment-type-label" color="blurple" variant="note" margin={{ bottom: 'none' }}>
            {treatmentTypeSetting?.label}
          </Typography>
          <IconCaretDown color="blurple" size="sm" data-testid="treatment-type-caret" />
        </Group>
      </Dropdown.Trigger>
      <Dropdown.Content padding={{ all: 'sm' }} data-testid="treatment-type-menu">
        <Group direction="column" gap="xs" data-testid="treatment-type-options">
          {allowedTreatmentTypeSettings?.map((treatmentTypeSetting, idx) => (
            <Button
              data-testid={`treatment-type-option-${idx}`}
              fullWidth
              tertiary
              key={treatmentTypeSetting.treatmentType}
              active={
                treatmentTypeSetting.treatmentTypeKey === treatmentType ||
                treatmentTypeSetting.treatmentType === treatmentType
              }
              disabled={
                treatmentTypeSetting.treatmentTypeKey === treatmentType ||
                treatmentTypeSetting.treatmentType === treatmentType ||
                isReadOnly
              }
              onClick={() => {
                setOpen(false);
                handleChangeTreatmentByValue(
                  treatmentTypeSetting.treatmentTypeKey ?? treatmentTypeSetting.treatmentType
                );
              }}
            >
              {treatmentTypeSetting.label}
            </Button>
          ))}
        </Group>
      </Dropdown.Content>
    </Dropdown>
  );
};
